/**
 * React Context provider for the shared NWC client.
 *
 * Ensures a single NWC client instance is shared across all pages,
 * so connection state (connected/disconnected, balance) persists
 * during navigation. Without this, each page would create its own
 * client and lose the WebSocket connection on unmount.
 *
 * Tier: 2 (Lightning features).
 *
 * @module contexts/NwcClientProvider
 */

import { createContext, useContext } from 'react';
import { useNwcClient } from '@/features/lightning/hooks/useNwcClient';
import type { UseNwcClientResult } from '@/features/lightning/hooks/useNwcClient';

const NwcClientContext = createContext<UseNwcClientResult | null>(null);

/** Props for NwcClientProvider. */
interface NwcClientProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Provide a shared NWC client to the component tree.
 *
 * Place inside AuthGate so the client only exists while the user
 * is authenticated. The client is created once and shared via context.
 *
 * @param props - Provider props with children
 * @returns Provider element
 */
export function NwcClientProvider({ children }: NwcClientProviderProps): React.ReactElement {
  const nwcClient = useNwcClient();

  return <NwcClientContext.Provider value={nwcClient}>{children}</NwcClientContext.Provider>;
}

/**
 * Access the shared NWC client from context.
 *
 * Must be called within NwcClientProvider. Returns the same client
 * instance regardless of which page/component calls it.
 *
 * @returns Shared NWC client result
 * @throws Error if called outside NwcClientProvider
 */
export function useSharedNwcClient(): UseNwcClientResult {
  const ctx = useContext(NwcClientContext);
  if (!ctx) {
    throw new Error('useSharedNwcClient must be used within NwcClientProvider');
  }
  return ctx;
}
